import { yupResolver } from '@hookform/resolvers/yup';
import Book from '@mui/icons-material/Book';
import {
  Autocomplete,
  Checkbox,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Switch,
  FormControlLabel,
  FormGroup,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import React, { Fragment, useEffect } from 'react';
import { AlertCircle } from 'react-feather';
import { Controller, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Alert, Modal, ModalBody, ModalHeader } from 'reactstrap';
import * as Yup from 'yup';
import { userRequest } from '../../../auth/requestMethods';
import RecalculateDeliverRuns from '../../common/DeliveryRunDialog';
import Breadcrumb from '../../common/breadcrumb';
import DateRange from '../../dateRange';
import { Helmet } from 'react-helmet-async';

const exceptionTypes = ['TEMPORARY_MENU', 'LEARNER_REDUCTION', 'MENU_SKU_TYPES'];

/**
 * Manage component handles the creation and editing of exceptions.
 * It manages the state of various forms, modals, and data required for exception creation.
 * It contains logic for fetching and updating data from APIs, as well as form validation and submission.
 *
 * @param {object} props - The component props.
 * @param {object} props.history - The history object for navigation.
 * @param {object} props.location - The location object containing the current URL.
 */

const Manage = ({ history, location }) => {
  const [selectionModel, setSelectionModel] = React.useState([]); // temp while modal is open
  const [savedSchools, setSavedSchools] = React.useState([]);
  const [selectedDeliveryRuns, setSelectedDeliveryRuns] = React.useState([]);
  const anchorRef = React.useRef(null);
  const [chosenSkuTypes, setChosenSkuTypes] = React.useState([]);
  const [dialogOpen, setDialogOpen] = React.useState(false);

  const [perPage, setPerPage] = React.useState(50);
  const [page, setPage] = React.useState(1);
  const [schoolsData, setSchoolsData] = React.useState({
    schools: [],
    count: 0,
  });
  const [deliveryRunsData, setDeliveryRunsData] = React.useState({
    deliveryRuns: [],
    count: 0,
  });
  const [schoolModal, setSchoolModal] = React.useState(false);
  const [schoolSearchInput, setSchoolSearchInput] = React.useState({
    menu: '',
    project: '',
  });

  const [schoolFilterOptions, setSchoolFilterOptions] = React.useState({
    menus: [],
    projects: [],
  });

  const [documentModal, setDocumentModal] = React.useState(false);
  const [deliveryRunModal, setDeliveryRunModal] = React.useState(false);

  const [exceptionId, setExceptionId] = React.useState(false);

  const [skuTypeOptions, setSkuTypeOptons] = React.useState([]);

  const [dateDetails, setDateDetails] = React.useState({
    updated: '',
    created: '',
    document: null,
  });

  const [document, setDocument] = React.useState(null);
  const [documentExists, setDocumentExists] = React.useState(false);
  const [createdBy, setCreatedBy] = React.useState(null);
  const [updatedBy, setUpdatedBy] = React.useState(null);
  const [cancelConfirmation, setCancelConfirmation] = React.useState(false);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    control,
    getValues,
    trigger,
    setValue,
    formState,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues,
  });

  /**
   * Sets the date range for a given form field.
   * @param {string} name - The name of the form field.
   * @param {string} val - The value of the form field.
   */
  function setDateRange(name, val) {
    let date = val ? (!isNaN(Date.parse(val)) ? dateToFormattedString(new Date(val)) + 'T00:00:00.000Z' : '') : '';
    setValue(name, date, { shouldDirty: true, shouldTouch: true });
  }

  const values = getValues();

  const buildQuery = React.useCallback(() => {
    return Object.entries(schoolSearchInput)
      .map(([key, value]) => {
        if (value !== '') {
          return encodeURIComponent(key) + '=' + encodeURIComponent(value);
        }
        return '';
      })
      .filter((Q) => !!Q)
      .join('&');
  }, [schoolSearchInput]);

  React.useEffect(() => {
    if (savedSchools.length && values.type === exceptionTypes[2])
      userRequest()
        .get(`/exceptions/search-sku-types?school_ids=${savedSchools.map((val) => val.id)}`)
        .then(({ data }) => {
          setSkuTypeOptons(data.skuTypes);
        })
        .catch((res) => console.log(res));
  }, [savedSchools, values.type]);

  React.useEffect(() => {
    trigger();
    const subscription = watch((value, { name }) => trigger(name));
    return () => subscription.unsubscribe();
  }, [watch, trigger]);

  React.useEffect(() => {
    let temp = location.pathname.split('/');
    if (temp[2] === 'edit') {
      setExceptionId(temp[3]);

      userRequest()
        .get(`/exceptions/retrieve/${temp[3]}`)
        .then(({ data: { exception, details, schools, document, createdBy, updatedBy } }) => {
          setCreatedBy(createdBy);
          setUpdatedBy(updatedBy);
          setDocument(document);
          setDocumentExists(true);

          reset({
            from_date: dateToFormattedString(new Date(exception.from_date)),
            to_date: dateToFormattedString(new Date(exception.to_date)),
            type: exception.type,
            learner_reduction: exception.type === 'LEARNER_REDUCTION' ? details.temporary_learners_fraction : 0,
            temporary_menu: exception.type === 'TEMPORARY_MENU' ? details.temporary_menu_id : '',
            document,
            is_active: exception.is_active,
          });
          setDateDetails({
            created: formatTime(new Date(exception.created_date_time)),
            updated: formatTime(new Date(exception.last_updated_date_time)),
          });
          setSavedSchools(schools);
          setSelectionModel(schools.map((s) => s.id));
          if (exception.type === 'MENU_SKU_TYPES') setChosenSkuTypes(details);
        })

        .catch((error) => console.log(error));
    }

    bulkAxios(['/menus/names', '/projects/names'])
      .then((all) => {
        setSchoolFilterOptions({
          menus: all[0].menus,
          projects: all[1].projects,
        });
      })
      .catch();
  }, [location.pathname, location.state, reset]);

  React.useEffect(() => {
    userRequest()
      .get(`/schools?per_page=${perPage}&page=${page}&${buildQuery()}`)
      .then(({ data }) => {
        setSchoolsData({
          count: data.total,
          schools: data.schools,
        });
      })
      .catch();
  }, [perPage, page, schoolSearchInput, buildQuery]);

  async function getAffectedDeliveryRuns() {
    const from = new Date(values.from_date);
    const to = new Date(values.to_date);
    const schools = savedSchools.map((obj) => obj.id).join(':');

    if (from.toString() !== 'Invalid Date' && to.toString() !== 'Invalid Date') {
      userRequest()
        .get(`/exceptions/delivery-runs/?from=${from.toISOString()}&to=${to.toISOString()}&schools=${schools}`)
        .then(({ data }) => {
          setDeliveryRunsData({
            count: data.total,
            deliveryRuns: data.deliveryRuns,
          });

          if (data.total === 0) {
            const save = handleSubmit(onSave);
            save();
          } else {
            setSelectedDeliveryRuns([]);
            setDeliveryRunModal(true);
          }
        })
        .catch();
    }
  }

  async function viewDoc() {
    userRequest()
      .get(`/exceptions/download-document?document_id=${document.id}`, { responseType: 'blob' })
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data], { type: response.headers['content-type'] }));
        window.open(url, '_blank');
      })
      .catch((res) => console.log('error...', res));
  }

  function bulkAxios(urls = null) {
    if (!urls) return urls;

    let promises = urls.map((req) =>
      userRequest()
        .get(req)
        .then(({ data }) => {
          return data;
        })
        .catch((error) => {
          console.log(error);
          return null;
        })
    );

    return Promise.all(promises);
  }

  function onSelectionModelChange(selections, fail) {
    // if (fail) return null;
    // TODO: keep display names too
    setSelectionModel(selections);
  }

  function openSchoolModal() {
    setSelectionModel(savedSchools.map((val) => val.id));
    setSchoolModal(true);
  }

  function onSave(data) {
    if (deliveryRunModal) setDeliveryRunModal(false);

    if (!exceptionId && !document) {
      toast.error('Please upload exception document before clicking create', {
        position: 'bottom-center',
        autoClose: 5000,
        closeOnClick: true,
        draggable: true,
      });
      return;
    }
    toast.info(`${exceptionId ? 'Updating...' : 'Creating...'}`, {
      isLoading: true,
      position: 'bottom-center',
      autoClose: 5000,
      theme: 'colored',
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      toastId: 'create',
    });

    // Update the data based on exception type
    if (data.type === 'LEARNER_REDUCTION') {
      data.learner_reduction = parseInt(data.learner_reduction); // Convert to integer
    } else if (data.type === 'TEMPORARY_MENU') {
      data.temporary_menu = values.temporary_menu;
    } else if (data.type === 'MENU_SKU_TYPES') {
      // Additional code to update SKU types
    }

    const doc = new FormData();
    if (document) {
      doc.append('file', document);
      doc.append(
        'schools',
        savedSchools.map((val) => val.id)
      );
      doc.append('deliveryRuns', selectedDeliveryRuns.join(','));
    } else {
      // Document is required but missing
      toast.error('Please upload a document before saving.', {
        position: 'bottom-center',
        autoClose: 5000,
        closeOnClick: true,
        draggable: true,
      });
      return;
    }

    if (data.type === 'MENU_SKU_TYPES') {
      doc.append(
        'sku_types',
        chosenSkuTypes.map((val) => val.sku_type)
      );
    }
    if (data.type === 'LEARNER_REDUCTION') {
      // Take the first value if it's an array
      data.learner_reduction = Array.isArray(data.learner_reduction)
        ? data.learner_reduction[0]
        : data.learner_reduction;
      data.learner_reduction = parseInt(data.learner_reduction);
    }

    // Append all data properties to the FormData
    Object.entries(data).forEach(([key, val]) => {
      if (key === 'document') doc.append(key, JSON.stringify(val));
      else doc.append(key, val);
    });

    const api = exceptionId ? `/exceptions/update/${exceptionId}` : '/exceptions';
    userRequest()
      .post(api, doc, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((res) => {
        toast.dismiss('create');
        toast.success(`Successfully ${exceptionId ? 'updated' : 'created'}`, {
          position: 'bottom-center',
          autoClose: 5000,
          closeOnClick: true,
          draggable: true,
        });
        history.push('/exceptions/manage');
      })
      .catch((error) => {
        toast.dismiss('create');
        toast.error(error.response?.data?.message || 'An error occurred while saving', {
          position: 'bottom-center',
          autoClose: 5000,
          closeOnClick: true,
          draggable: true,
        });
        console.error('Save Error:', error);
      });
  }

  return (
    <Fragment>
      <Breadcrumb icon={<Book />} title={exceptionId ? 'Edit' : 'Create'} parent="Exceptions" />
      <Helmet>
        <title>PFSA | Exceptions | {exceptionId ? 'Edit' : 'Create'}</title>
      </Helmet>
      <div className="col-sm-12">
        <div className="card">
          <div className="card-body">
            <form className="theme-form" onSubmit={handleSubmit(onSave)}>
              <div className="form-group row">
                <p>
                  <b>Schools</b>
                </p>
                <DataGrid
                  autoHeight
                  hideFooter
                  loading={false}
                  rows={savedSchools}
                  columns={quickSchoolsColumns}
                  disableSelectionOnClick
                />
                <div className="m-0 p-0">
                  <button onClick={openSchoolModal} className="btn btn-secondary mt-4" type="button">
                    Select Schools
                  </button>
                </div>
              </div>
              <div className="form-group row">
                <DateRange
                  fromError={values.from_date ? null : 'Required'}
                  toError={values.to_date ? null : 'Required'}
                  fromOnChange={(val) => setDateRange('from_date', val)}
                  toOnChange={(val) => setDateRange('to_date', val)}
                  from={values.from_date}
                  to={values.to_date}
                  label="Date Range"
                  disabled={false}
                />
              </div>
              <br />

              <div className="form-group row">
                <Controller
                  control={control}
                  name="type"
                  render={({ field }) => (
                    <FormControl error={!!errors.type?.message}>
                      <InputLabel>Exception Type</InputLabel>
                      <Select
                        name={field.name}
                        value={field.value}
                        onBlur={field.onBlur}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                        }}
                        label="Exception Type"
                      >
                        {exceptionTypes.map((val, i) => (
                          <MenuItem key={i} value={val}>
                            {val
                              .split('_')
                              .map((v) => v.charAt(0).toUpperCase() + v.slice(1, 100).toLowerCase())
                              .join(' ')}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </div>
              {values.type === 'TEMPORARY_MENU' && (
                <div className="form-group row">
                  <Controller
                    control={control}
                    name="temporary_menu"
                    render={({ field }) => (
                      <FormControl error={!!errors.temporary_menu?.message}>
                        <InputLabel id="demo-simple-select-helper-label">Temporary Menu</InputLabel>
                        <Select
                          name={field.name}
                          value={field.value}
                          onBlur={field.onBlur}
                          onChange={(e) => {
                            field.onChange(e.target.value);
                          }}
                          labelId="demo-simple-select-helper-label"
                          label="Temporary Menu"
                        >
                          <MenuItem value={''}>- Select -</MenuItem>
                          {schoolFilterOptions.menus.map((val, i) => (
                            <MenuItem key={i} value={val.id}>
                              {val.display_name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </div>
              )}
              {values.type === 'MENU_SKU_TYPES' && (
                <div className="form-group row">
                  {chosenSkuTypes.length <= 0 && (
                    <Alert className="d-flex gap-3" color="secondary dark">
                      <AlertCircle />
                      <p>{savedSchools.length <= 0 ? 'Please select schools first' : 'Please add SKU Types below'}</p>
                    </Alert>
                  )}
                  <Autocomplete
                    multiple
                    options={skuTypeOptions}
                    className="p-0 "
                    value={chosenSkuTypes}
                    isOptionEqualToValue={(opt, val) => opt.sku_type === val.sku_type}
                    renderOption={(props, option, { selected }) => (
                      <li {...props}>
                        <Checkbox style={{ marginRight: 8 }} checked={selected} />
                        {option.display_name}
                      </li>
                    )}
                    onChange={(e, val) => setChosenSkuTypes(val)}
                    getOptionLabel={(option) => option.display_name}
                    renderInput={(params) => <TextField {...params} label="Add SKU Type" />}
                  />
                </div>
              )}
              <br />
              {values.type === 'LEARNER_REDUCTION' && (
                <div className="form-group row">
                  <FormControl
                    sx={{
                      '& .css-o9k5xi-MuiInputBase-root-MuiOutlinedInput-root': {
                        width: '15%',
                      },
                    }}
                    variant="outlined"
                  >
                    <InputLabel>Learner Reduction</InputLabel>
                    <OutlinedInput
                      type="number"
                      {...register('learner_reduction')}
                      error={formState.dirtyFields.learner_reduction ? !!errors.learner_reduction?.message : false}
                      endAdornment={<InputAdornment position="end">%</InputAdornment>}
                      label="Learner Reduction"
                      inputProps={{
                        style: {
                          color: 'black',
                        },
                      }}
                    />
                  </FormControl>
                  {savedSchools.map((sc, ind) => (
                    <div key={ind}>
                      <p className="mt-2 p-0">
                        {sc.display_name}: {sc.learner_count} Learners | Adjusted number:{' '}
                        {Math.ceil(sc.learner_count * (100 - values.learner_reduction) * 0.01)}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              <p>
                <b>Exception Document</b>
              </p>
              <div>
                <span>{document?.filename ?? document?.name}</span>
                <br />
                {exceptionId && (
                  <>
                    <button className="btn btn-outline-secondary me-1" type="button" onClick={viewDoc}>
                      View / Download
                    </button>
                    {/*<button className="btn btn-outline-secondary me-1" type="button" onClick={downloadDoc}>
                      Download
                    </button>*/}
                  </>
                )}
                <button
                  onClick={() => {
                    setDocumentModal(true);
                  }}
                  className="btn btn-outline-secondary me-1"
                  type="button"
                >
                  {document?.filename || document?.name ? 'Upload New' : 'Upload'}
                </button>
              </div>
              <div>
                <br />
                {!document && !exceptionId && (
                  <span className="text-danger">Please upload exception document before clicking create.</span>
                )}
              </div>

              {exceptionId && (
                <div className="container" style={{ marginLeft: 0 }}>
                  <div className="row">
                    <div className="col-2" style={{ paddingLeft: 0 }}>
                      Created:{' '}
                    </div>
                    <div className="col-10">
                      {`${createdBy?.name} ${createdBy?.surname}`} {dateDetails.created}
                    </div>
                  </div>
                </div>
              )}
              {exceptionId && updatedBy && (
                <div className="container" style={{ marginLeft: 0 }}>
                  <div className="row">
                    <div className="col-2" style={{ paddingLeft: 0 }}>
                      Last Updated:{' '}
                    </div>
                    <div className="col-10">
                      {`${updatedBy?.name} ${updatedBy?.surname}`} {dateDetails.updated}
                    </div>
                  </div>
                </div>
              )}

              {!!exceptionId && (
                <div className="d-flex justify-content-start my-4">
                  <FormGroup>
                    <FormControlLabel
                      control={<Switch {...register('is_active')} checked={watch('is_active')} />}
                      label="Active Status"
                      sx={{ margin: 0 }}
                      labelPlacement="start"
                    />
                  </FormGroup>
                </div>
              )}

              <br />
              <div>
                <button
                  className="btn btn-primary me-1"
                  type="button"
                  onClick={() => {
                    getAffectedDeliveryRuns();
                  }}
                >
                  {exceptionId ? 'Save' : 'Create'}
                </button>

                <button
                  onClick={() => {
                    if (!cancelConfirmation) {
                      setCancelConfirmation(true);
                    } else {
                      setDocumentModal(false);
                      setDocument(null);
                    }
                  }}
                  className="btn btn-secondary me-1"
                  type="button"
                >
                  Cancel
                </button>
                {cancelConfirmation && (
                  <ConfirmationDialog
                    message="Are you sure you want to cancel?"
                    onConfirm={() => {
                      setDocumentModal(false);
                      setDocument(null);
                      setCancelConfirmation(false);
                    }}
                    onCancel={() => setCancelConfirmation(false)}
                  />
                )}
              </div>

              {/* Modals ======================================================================================================================== */}
              <Modal
                isOpen={schoolModal}
                toggle={() => {
                  setSchoolModal(false);
                  reset(defaultValues, {});
                }}
                size="xl"
                centered={true}
              >
                <ModalHeader
                  toggle={() => {
                    setSchoolModal(false);
                    reset(defaultValues, {});
                  }}
                >
                  Select Schools
                </ModalHeader>
                <ModalBody>
                  <div className="card-body">
                    <div className="form-group row">
                      <FormControl>
                        <InputLabel id="demo-simple-select-helper-label">Project</InputLabel>
                        <Select
                          name="project"
                          value={schoolSearchInput.project}
                          onChange={({ target }) =>
                            setSchoolSearchInput({
                              ...schoolSearchInput,
                              project: target.value,
                            })
                          }
                          labelId="demo-simple-select-helper-label"
                          id="demo-simple-select-helper"
                          label="Project"
                        >
                          <MenuItem value={''}>- Select -</MenuItem>
                          {schoolFilterOptions.projects.map((val) => (
                            <MenuItem key={val.id} value={val.display_name}>
                              {val.display_name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>
                    <br />
                    <div className="form-group row">
                      <FormControl>
                        <InputLabel id="demo-simple-select-helper-label">Menu</InputLabel>
                        <Select
                          name="menu"
                          value={schoolSearchInput.menu}
                          onChange={({ target }) =>
                            setSchoolSearchInput({
                              ...schoolSearchInput,
                              menu: target.value,
                            })
                          }
                          labelId="demo-simple-select-helper-label"
                          id="demo-simple-select-helper"
                          label="Menu"
                        >
                          <MenuItem value={''}>- Select -</MenuItem>
                          {schoolFilterOptions.menus.map((val) => (
                            <MenuItem key={val.id} value={val.display_name}>
                              {val.display_name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>
                    <br />
                    <br />
                    <div className="form-group row">
                      <DataGrid
                        rows={schoolsData.schools}
                        columns={schoolsColumns}
                        paginationMode="server"
                        rowCount={schoolsData.count}
                        pageSize={perPage}
                        onPageSizeChange={(size) => setPerPage(size)}
                        onPageChange={(pg) => setPage(++pg)}
                        autoHeight
                        rowsPerPageOptions={[5, 10, 20, 50]}
                        isRowSelectable={(params) => true}
                        loading={false}
                        onSelectionModelChange={onSelectionModelChange}
                        selectionModel={selectionModel}
                        checkboxSelection={true}
                        disableSelectionOnClick
                        experimentalFeatures={{ newEditingApi: true }}
                      />
                    </div>

                    <div className="mt-4 p-0">
                      <button
                        className="btn btn-primary me-1"
                        type="button"
                        onClick={() => {
                          setSavedSchools(
                            selectionModel.map((val) => {
                              return schoolsData.schools.find((value) => value.id === val);
                            })
                          );

                          setSelectionModel([]);
                          setSchoolModal(false);
                        }}
                      >
                        Save
                      </button>
                      <button
                        className="btn btn-secondary me-1"
                        onClick={() => {
                          setSelectionModel([]);
                          setSchoolModal(false);
                        }}
                        type="button"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </ModalBody>
              </Modal>

              <Modal
                isOpen={deliveryRunModal}
                toggle={() => {
                  setDeliveryRunModal(false);
                }}
                size="l"
                centered={true}
              >
                <ModalHeader
                  toggle={() => {
                    setDeliveryRunModal(false);
                  }}
                >
                  Update Delivery Runs
                </ModalHeader>
                <ModalBody>
                  <div className="card-body">
                    <p>
                      The following delivery runs will be affected by this change. Please select which delivery runs to
                      recalculate:
                    </p>
                    <div>
                      <div className="form-group row">
                        <DataGrid
                          rows={deliveryRunsData.deliveryRuns}
                          columns={deliveryRunColumns}
                          rowCount={deliveryRunsData.count}
                          autoHeight
                          hideFooter
                          isRowSelectable={() => true}
                          loading={false}
                          onSelectionModelChange={setSelectedDeliveryRuns}
                          selectionModel={selectedDeliveryRuns}
                          checkboxSelection={true}
                          disableSelectionOnClick
                          experimentalFeatures={{ newEditingApi: true }}
                        />
                      </div>

                      <div className="mt-4 p-0">
                        <button onClick={handleSubmit(onSave)} className="btn btn-primary me-1" type="button">
                          {selectedDeliveryRuns.length ? 'Recalculate' : 'Ignore'}
                        </button>
                        <button
                          onClick={() => {
                            setDeliveryRunModal(false);
                          }}
                          className="btn btn-secondary me-1"
                          type="button"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                </ModalBody>
              </Modal>

              <Modal
                isOpen={documentModal}
                toggle={() => {
                  setDocumentModal(false);
                }}
                size="l"
                centered={true}
              >
                <ModalHeader
                  toggle={() => {
                    setDocumentModal(false);
                  }}
                >
                  Upload Document
                </ModalHeader>
                <ModalBody>
                  <div className="card-body">
                    {documentExists && (
                      <Alert className="d-flex gap-3" color="warning dark">
                        <AlertCircle />
                        <p>A document was already uploaded, which will be deleted when a new document is uploaded</p>
                      </Alert>
                    )}
                    <div>
                      <label className="btn justify-content-center d-flex border" htmlFor="fileInput">
                        {document?.filename ? document.filename : document?.name ? document.name : 'Select file'}
                      </label>
                      <input
                        id="fileInput"
                        style={{ display: 'none' }}
                        type="file"
                        accept=".pdf"
                        onChange={(e) => {
                          const selectedFile = e.target.files[0];
                          setDocument(selectedFile);
                          console.log('Selected document:', selectedFile);
                        }}
                      />
                    </div>
                    <div className="mt-4 p-0">
                      <button onClick={() => setDocumentModal(false)} className="btn btn-primary me-1" type="button">
                        Upload
                      </button>
                      <button
                        onClick={() => {
                          setDocumentModal(false);
                          setDocument(null);
                        }}
                        className="btn btn-secondary me-1"
                        type="button"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </ModalBody>
              </Modal>
            </form>
          </div>
        </div>
        <RecalculateDeliverRuns open={dialogOpen} setOpen={() => setDialogOpen((pr) => !pr)} />
      </div>
    </Fragment>
  );
};

const quickSchoolsColumns = [
  {
    field: 'display_name',
    headerName: 'Schools',
    flex: 1,
  },
];

const schoolsColumns = [
  {
    field: 'display_name',
    headerName: 'School Name',
    flex: 1,
  },
  {
    field: 'learner_count',
    headerName: 'Learners',
    flex: 1,
  },
  {
    field: 'project',
    headerName: 'Project',
    flex: 1,
  },
  {
    field: 'menu',
    headerName: 'Menu',
    flex: 1,
    renderCell: ({ value }) => {
      return (
        <Link to={`/menu-details/manage?display_name=${value}`}>
          <u>{value}</u>
        </Link>
      );
    },
  },
];

const deliveryRunColumns = [
  {
    field: 'id',
    headerName: 'Delivery Run ID',
    flex: 1,
  },
  {
    field: 'runType',
    headerName: 'Type',
    flex: 1,
  },
];

const defaultValues = {
  from_date: dateToFormattedString(new Date()),
  to_date: dateToFormattedString(new Date()),
  type: 'LEARNER_REDUCTION',
  learner_reduction: 0,
  temporary_menu: '',
  is_active: true,
};

const validationSchema = Yup.object().shape({
  from_date: Yup.string().required('Required'),
  to_date: Yup.string().required('Required'),
  type: Yup.string().required('Required'),

  temporary_menu: Yup.string().when('type', {
    is: 'TEMPORARY_MENU',
    then: Yup.string().required('Required'),
  }),
  learner_reduction: Yup.string().when('type', {
    is: 'LEARNER_REDUCTION',
    then: Yup.string()
      .transform((value) => (parseInt(value) <= 0 ? NaN : value))
      .typeError('Incorrect input')
      .required('Required'),
  }),
});

/**
 * Returns a formatted string of a given date object.
 *
 * @param {Date} d - The date object
 * @return {string} The formatted date string in the format "dd/mm/yyyy"
 */
function dateToFormattedString(d) {
  var yyyy = d.getFullYear().toString();
  var mm = (d.getMonth() + 1).toString(); // getMonth() is zero-based
  var dd = d.getDate().toString();

  return yyyy + '-' + (mm[1] ? mm : '0' + mm[0]) + '-' + (dd[1] ? dd : '0' + dd[0]);
}

export default Manage;
// ...
//are you sure you want to Cancel pop up
const ConfirmationDialog = ({ message, onConfirm, onCancel }) => {
  return (
    <Modal isOpen={true} size="sm" centered={true} backdrop="static" keyboard={false} dialogClassName="text-center">
      <ModalHeader>
        <div className="text-center">Cancel Changes</div>
      </ModalHeader>
      <ModalBody>
        <p>{message}</p>

        <div className="text-center">
          <Link to={`${process.env.PUBLIC_URL}/exceptions/manage`}>
            <button onClick={onConfirm} className="btn btn-secondary" type="button">
              Yes
            </button>
          </Link>
          <span
            style={{
              display: 'inline-block',
              width: '20px',
              height: '20px',
            }}
          ></span>
          <button onClick={onCancel} className="btn btn-secondary" type="button">
            No
          </button>
        </div>
      </ModalBody>
    </Modal>
  );
};

function formatTime(dateString) {
  const dateObject = new Date(dateString);

  const options = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false, // Use 24-hour format
  };

  let formattedDate = dateObject.toLocaleDateString('en-GB', options);

  const parts = formattedDate.split(', ');
  const [dayMonthYear, time] = parts;
  const [day, month, year] = dayMonthYear.split('/');
  const rearrangedDate = `${year}-${month}-${day} ${time}`;
  return rearrangedDate;
}
